/**
 * NgwEditorContent — `IEditorContent` 의 NgwEditor 구현 (PR-N1).
 *
 * NgwEditor 의 `EditorHandle` 24 메서드 중 본 단계에 매핑되는 것:
 *   - `setHTML(html)` / `getHTML({ includeStyles })` / `insertText(text)` / `insertHTML(html)`
 *   - `getText()` (isEmpty 의 trim 검사 용도)
 *   - `getEditorDOM()` (getBody 의 ProseMirror view.dom 반환)
 *
 * **DOM context**: 모든 접근은 `adapter.lifecycle.getHandle()` 경유. parent document.* 직접
 * 접근 금지.
 *
 * **setDefaultStyle 의 key 재마운트 (D-N2)**: camelCase → EditorDefaults 변환 후
 * `lifecycle.requestRemount(nextDefaults)` 호출. 직전 본문 보존/복원은 requestRemount →
 * handleEditorReady(onReady) 가 일괄 처리한다. undo 손실은 의도된 동작 (pr-n0-inventory §4 D-N2).
 */

import { type EditorDefaults } from '@ngweditor/react'
import DOMPurify, { type Config as DOMPurifyConfig } from 'dompurify'
import type { IEditorContent } from '../IEditorContent'
import type { EditorDefaultStyle, SetDataOptions } from '../types'
import { buildAvailabilityHtml } from './availabilityHtml'
import { isDev } from './editorEnv'
import { extractVariantStyleCssFromDoc, inlineVariantCssIntoRoot } from './extractVariantStyleCss'
import { unwrapEmailBodyHTMLDOM, wrapEmailBodyHTML } from './extensions/mailRegions'
import type { NgwEditorAdapter } from './NgwEditorAdapter'
import { NGW_DEFAULT_LINE_HEIGHT } from './NgwEditorLifecycle'
import { normalizeHrInlineStyleDOM } from './normalizeHrInlineStyle'
import { wrapRegionTextWithDefaultColorDOM } from './parseBodyDefaultStyle'

/**
 * C (ngwEditor-migration-0.2.12): 어댑터 전용 격리 DOMPurify 인스턴스.
 *
 * `DOMPurify(window)` 로 별도 인스턴스 생성하여 글로벌 싱글톤 hook 누수를 차단.
 * 기존 캘린더(NoteDetail/EventDetail) / 할일(TodoDetails) 의 DOMPurify 사용처에 영향 없음
 * (Cycle 3 검증 + Cycle 6 보완).
 *
 * 적용 정책 (design.md §6.1 P-C-CFG):
 *   - ALLOWED: 메일 도메인 핵심 attr (`nine-mail-id`, `contenteditable`, `dir`, `class`, `id`,
 *     `style`, `rel`, `target`, `role`, `aria-label`) + `data-*` (`data-nine-*` 보존)
 *   - FORBIDDEN: `<script>`, `<iframe>`, `<object>`, `<embed>`, `<form>`, `<meta>`, `<link>`
 *     + `srcdoc`, `formaction` attr
 *   - `<style>` 태그는 코어 setHTML 단계에서 추가 제거되므로 별도 명시 불요
 *
 * afterSanitizeAttributes 훅: `<a target="_blank">` 에 `rel="noopener noreferrer"` 강제 주입
 * (reverse tabnabbing 방어). 인스턴스 격리라 다른 DOMPurify 사용처와 무관.
 */
const ngwPurify = DOMPurify(window)
ngwPurify.addHook('afterSanitizeAttributes', (node) => {
  // Element 만 처리 (Text/Document fragment 등 제외).
  if (!(node instanceof HTMLElement)) return
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer')
  }
})

const NGW_PURIFY_CONFIG: DOMPurifyConfig = {
  ADD_ATTR: [
    'nine-mail-id',
    'contenteditable',
    'dir',
    'class',
    'id',
    'style',
    'rel',
    'target',
    'role',
    'aria-label',
  ],
  ALLOW_DATA_ATTR: true,
  KEEP_CONTENT: true,
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'meta', 'link'],
  FORBID_ATTR: ['srcdoc', 'formaction'],
  // RETURN_TRUSTED_TYPE: false — 기본값이지만 명시하여 sanitize 반환 타입을 string 으로 좁힘.
  RETURN_TRUSTED_TYPE: false,
}

/**
 * 외부 메일 HTML 을 setHTML 호출 전에 sanitize. 메일 본문은 외부 발신자가 임의 HTML 을
 * 보낼 수 있는 신뢰 불가 입력 (QA Q7-2).
 *
 * 적용 순서 (design.md §6.1 P-C-ORDER 채택):
 *   `sanitizeBeforeSetHTML(html) → unwrapEmailBodyHTML → sanitizeImgDimensions → setHTML`
 *
 * 상위 파이프라인 `getSafeHTML` (utils/index.ts) 가 script / on* / javascript: 는 처리하나
 * iframe / object / embed / form / meta / link 는 미처리 (Cycle 3 C-0 verdict). 본 함수는
 * defense-in-depth + 추가 위협 차단 목적.
 *
 * @internal 단위 테스트에서 직접 검증.
 */
export function sanitizeBeforeSetHTML(html: string): string {
  if (html.length === 0) return html
  return ngwPurify.sanitize(html, NGW_PURIFY_CONFIG)
}

/**
 * `<img>` 태그의 width/height attribute 를 NgwEditor schema 가 해석 가능한
 * 순수 정수로 정규화한다.
 *
 * 배경: `@ngweditor/react@0.2.10` 의 image NodeSpec parseDOM 은
 * `element.style.width` 가 비어 있으면 `element.getAttribute('width')` 결과를
 * 문자열 그대로 node attrs 에 저장한다. 이메일 본문에 흔히 등장하는
 * `width="100%"`, `width="auto"`, `width="50em"` 등은 ImageNodeView 내부의
 * `Math.min(rawWidth, editorMax)` 에서 NaN 으로 평가되어 React 가 인라인 style 에
 * `width: NaN` 을 적용하려다 콘솔 경고를 낸다.
 */
export function sanitizeImgDimensions(html: string): string {
  if (html.length === 0 || !/<img\b/i.test(html)) return html

  const doc = new DOMParser().parseFromString(html, 'text/html')
  const modified = sanitizeImgDimensionsDOM(doc)
  if (!modified) return html

  return /<html[\s>]/i.test(html) ? doc.documentElement.outerHTML : doc.body.innerHTML
}

/**
 * sanitizeImgDimensions 의 DOM variant — 문자열 재파싱 없이 이미 파싱된 트리의 img width/height 를
 * 직접 정규화한다. reply 인용/서명 DOM 직결 경로(fragmentToContainer)가 사용한다. 문자열 경로와
 * 동일 로직(normalizeImgDimensionAttr)을 공유해 결과 parity 를 보장한다.
 * @returns 하나라도 수정됐으면 true (문자열 경로의 "미변경 시 원본 반환" 최적화에 사용).
 */
export function sanitizeImgDimensionsDOM(root: ParentNode): boolean {
  let modified = false
  for (const img of Array.from(root.querySelectorAll('img'))) {
    if (normalizeImgDimensionAttr(img, 'width')) modified = true
    if (normalizeImgDimensionAttr(img, 'height')) modified = true
  }
  return modified
}

function normalizeImgDimensionAttr(img: HTMLImageElement, name: 'width' | 'height'): boolean {
  const value = img.getAttribute(name)
  if (value == null) return false
  const trimmed = value.trim()
  if (/^\d+$/.test(trimmed)) return false
  const pxMatch = /^(\d+)(?:\.\d+)?px$/i.exec(trimmed)
  if (pxMatch != null) {
    img.setAttribute(name, pxMatch[1]!)
    return true
  }
  img.removeAttribute(name)
  return true
}

/**
 * editor-image-tools (R1-F6) — cross-editor 마이그레이션. TinyMCE 는 `data-nine-rotate` 를
 * index(0~3)로 저장하지만 ngwEditor 는 degrees(0/90/180/270)로 해석한다. TinyMCE 로 저장된
 * draft 를 ngwEditor 로 열 때 `1`(index, 90°) 이 1° 로 오해석되는 회귀를 막는다.
 *
 * ngwEditor 의 rotateImage 는 항상 {0,90,180,270} 만 생성하므로, 비-0 값 {1,2,3} 은 TinyMCE
 * index 로 안전 판정 가능(도메인 분리, 오탐 0). {1,2,3} → ×90 변환.
 */
export function migrateLegacyImageRotate(html: string): string {
  if (html.length === 0 || !/data-nine-rotate/i.test(html)) return html
  const doc = new DOMParser().parseFromString(html, 'text/html')
  if (!migrateLegacyImageRotateDOM(doc)) return html
  return /<html[\s>]/i.test(html) ? doc.documentElement.outerHTML : doc.body.innerHTML
}

/**
 * migrateLegacyImageRotate 의 DOM variant — 문자열 재파싱 없이 이미 파싱된 트리의
 * `data-nine-rotate` index({1,2,3})를 degrees(×90)로 직접 변환한다. setData DOM 통합 경로
 * (draft-setdata-dom-normalize)와 문자열 경로가 동일 로직을 공유해 결과 parity 를 보장한다.
 * @returns 하나라도 수정됐으면 true.
 */
export function migrateLegacyImageRotateDOM(root: ParentNode): boolean {
  let modified = false
  for (const img of Array.from(root.querySelectorAll('img[data-nine-rotate]'))) {
    const raw = img.getAttribute('data-nine-rotate')
    if (raw === '1' || raw === '2' || raw === '3') {
      img.setAttribute('data-nine-rotate', String(Number.parseInt(raw, 10) * 90))
      modified = true
    }
  }
  return modified
}

// `div.nine-pg` 는 legacy nine_tinymce_desktop 의 paragraph 등가물. ngweditor 의 paragraph
// schema 는 `<p>` 만 받지만 PM 이 외곽 div 를 유지한 채 자식만 paragraph wrap 하는 경로가
// 있어, `<div class="nine-pg"><br></div>` 도 trailingBreak 중복을 트리거한다 — selector 에
// 포함하여 단독 br 제거.
const EMPTY_BLOCK_BR_SELECTOR = 'p, h1, h2, h3, h4, h5, h6, li, blockquote, div.nine-pg'

/**
 * editor-image-tools (SE1) — 전송 직전 제거할 이미지 내부 메타 속성.
 * TinyMCE `_preprocessNode`([nine_tinymce_desktop.js:1461-1509]) 와 동일 범위.
 */
const NINE_IMAGE_META_ATTRS = [
  'data-nine-rotate',
  'data-nine-flip-h',
  'data-nine-flip-v',
  'data-nine-size',
  'data-nine-src',
  'data-nine-resize',
] as const

/**
 * paragraph 또는 비슷한 block element 의 자식이 단 1개의 `<br>` 뿐인 경우 그 `<br>` 를 제거한다.
 *
 * 배경: ProseMirror EditorView 는 paragraph 의 마지막 inline 이 hard_break (`<br>`) 거나
 * paragraph 가 비어있을 때 cursor 표시용 placeholder `<br class="ProseMirror-trailingBreak">`
 * 를 자동 부착. 결과적으로 입력이 `<p><br></p>` 형태면 editor 표시 DOM 이
 * `<p><br><br class="ProseMirror-trailingBreak"></p>` 가 되어 빈 줄이 2 개로 보인다.
 *
 * `<br>` 를 strip 하여 빈 paragraph 가 되면 PM 이 trailingBreak 1 개만 자동 부착 → 시각적
 * 빈 줄 1 개 (정상 cursor 표시). `<p>본문<br></p>` 같이 trailing `<br>` 가 콘텐츠와 함께
 * 있는 케이스는 자식이 1 개뿐이 아니므로 strip 대상 아님 — 보존.
 *
 * 본 정규화는 NgwEditorMailRegion.htmlToContainer (region.set 진입점) 와 동일 정책을
 * setData 진입 흐름에도 적용하기 위해 helper 로 분리. 양쪽 호출처가 본 export 재사용.
 */
export function stripEmptyBlockTrailingBr(html: string): string {
  if (html.length === 0 || !/<br\b/i.test(html)) return html

  const doc = new DOMParser().parseFromString(html, 'text/html')
  if (!stripEmptyBlockTrailingBrDOM(doc)) return html

  return /<html[\s>]/i.test(html) ? doc.documentElement.outerHTML : doc.body.innerHTML
}

/**
 * stripEmptyBlockTrailingBr 의 DOM variant — 문자열 재파싱 없이 이미 파싱된 트리에서
 * 자식이 단독 `<br>` 뿐인 block 의 `<br>` 를 직접 제거한다. setData DOM 통합 경로
 * (draft-setdata-dom-normalize)와 문자열 경로가 동일 로직을 공유해 결과 parity 를 보장한다.
 * @returns 하나라도 수정됐으면 true.
 */
export function stripEmptyBlockTrailingBrDOM(root: ParentNode): boolean {
  let modified = false
  for (const block of Array.from(root.querySelectorAll(EMPTY_BLOCK_BR_SELECTOR))) {
    if (block.childNodes.length === 1 && block.firstChild?.nodeName === 'BR') {
      block.removeChild(block.firstChild)
      modified = true
    }
  }
  return modified
}

/**
 * setData / setDataFromDOM 공통 — 이미 파싱된 본문 컨테이너(Document.body 또는 임시 div)에
 * 정규화 6종(+조건부 wrap)을 **OLD 문자열 체인과 동일 순서**로 직접 적용한다
 * (draft-setdata-dom-normalize). 문자열 경로(각 함수가 개별 parse/serialize)와 달리 단일 트리에서
 * 순차 적용 후 호출부가 1회 직렬화 → 반복 재파싱 제거. 결과는 OLD 체인과 byte-identical(= 동일 PM doc).
 *
 * 순서(★parity 핵심 — EmailForm OLD 와 동일):
 *   (0) reopenBodyColor 시 wrapRegionTextWithDefaultColorDOM — setData 진입 '전' = DOMPurify 보다 먼저.
 *   (1) sanitizeBeforeSetHTML(DOMPurify IN_PLACE) — Phase 1.3 실측: string sanitize 와 byte-identical.
 *   (2) unwrapEmailBodyHTMLDOM (3) normalizeHrInlineStyleDOM (4) sanitizeImgDimensionsDOM
 *   (5) migrateLegacyImageRotateDOM (6) stripEmptyBlockTrailingBrDOM.
 */
export function normalizeSetDataDOM(container: Element, opts?: SetDataOptions): void {
  if (opts?.reopenBodyColor != null && opts.reopenBodyColor.trim().length > 0) {
    wrapRegionTextWithDefaultColorDOM(container, opts.reopenBodyColor)
  }
  ngwPurify.sanitize(container, { ...NGW_PURIFY_CONFIG, IN_PLACE: true })
  unwrapEmailBodyHTMLDOM(container)
  normalizeHrInlineStyleDOM(container)
  sanitizeImgDimensionsDOM(container)
  migrateLegacyImageRotateDOM(container)
  stripEmptyBlockTrailingBrDOM(container)
}

export class NgwEditorContent implements IEditorContent {
  private readonly adapter: NgwEditorAdapter

  constructor(adapter: NgwEditorAdapter) {
    this.adapter = adapter
  }

  setData(html: string, opts?: SetDataOptions): Promise<void> {
    if (this.adapter.lifecycle.getHandle() == null) {
      return Promise.reject(new Error('NgwEditorContent.setData: editor not ready'))
    }
    // draft-setdata-dom-normalize: 정규화 6종(+조건부 wrap)을 각자 재파싱하던 문자열 체인을
    // 맨 앞 1회 파싱 → DOM 위 순차 정규화 → 1회 직렬화로 통합(파싱 횟수 축소). 결과는 기존 문자열
    // 체인과 byte-identical(= 동일 PM doc). DOMPurify 는 html/head 를 strip 하므로 직렬화 경계는
    // 항상 body-level(`doc.body.innerHTML`) — 기존 체인의 최종 산출과 동일.
    const doc = new DOMParser().parseFromString(html, 'text/html')
    normalizeSetDataDOM(doc.body, opts)
    return this.applyNormalizedContent(doc.body.innerHTML, opts)
  }

  setDataFromDOM(root: Document | DocumentFragment, opts?: SetDataOptions): Promise<void> {
    if (this.adapter.lifecycle.getHandle() == null) {
      return Promise.reject(new Error('NgwEditorContent.setDataFromDOM: editor not ready'))
    }
    // draft/resend 재열기 DOM 직결 — convertCidNgwToDocument(Document) / getSafeElement(Fragment)이
    // 이미 파싱한 DOM 을 재파싱 없이 그대로 정규화한다. 직렬화 경계는 setData(string)과 동일
    // (body/container innerHTML). Document 는 body 를, Fragment 는 임시 컨테이너를 작업 트리로 쓴다.
    let container: Element
    if (root.nodeType === Node.DOCUMENT_NODE) {
      container = (root as Document).body
    } else {
      container = document.createElement('div')
      container.appendChild(root)
    }
    normalizeSetDataDOM(container, opts)
    // DOM 직결(0.4.21) — handle.setHTML 이 Element 를 직접 받아 string→DOM 재파싱(2.2MB WebKit ~4.0s)을
    // 생략한다. unwrap/<style> 제거 / 표 width reconcile / loneBr / 문단 margin strip / PM 파싱 / 기본색
    // strip 은 전부 라이브러리가 문자열 경로와 동일하게 수행하므로(표 포함 메일도 폴백 없이 처리), 어댑터는
    // 정규화된 컨테이너를 그대로 넘긴다. 입력 자식은 setHTML 이 소비하지만 applyNormalizedContent 가
    // schema fallback 재사용을 위해 clone 을 넘기므로 호출부 영향 없음.
    return this.applyNormalizedContent(container, opts)
  }

  /**
   * setHTML 적용 공통부 — 정규화된 본문을 문자열(setData) 또는 이미 파싱된 DOM(setDataFromDOM)으로
   * 받아 적용한다. legacy draft schema fallback(nbody wrap) 포함.
   *
   * DOM(Element) 입력: 0.4.21 setHTML 이 Element 를 직접 받아 string→DOM 재파싱을 생략한다. setHTML 은
   * 입력 노드의 자식을 에디터로 소비하므로, 아래 schema fallback 이 원본을 재사용할 수 있도록 clone 을
   * 넘긴다(clone 은 토크나이징이 없어 수 ms — 대용량에서도 재파싱 ~4s 대비 무시 가능).
   *
   * A-1.fallback (ngwEditor-migration-0.2.12): A-1 이 doc.content 시퀀스
   * ('nbody blank_sign? signature? blank_msg? quoted_header? quoted_body?')를 강제하므로,
   * pre-region 시점에 저장된 region 마커 없는 plain HTML 이 schema validation 실패하는 edge case
   * 를 차단한다. 사용자 draft 가 빈 에디터로 뜨는 시나리오 방지 (Cycle 2 critical). 대부분의 plain
   * HTML 은 unwrapEmailBodyHTML 의 nbody 합성 또는 PMDOMParser.parse 의 fillContent 로 보강되므로
   * fallback 실발화는 드물지만 데이터 무결성 안전망으로 유지.
   */
  private applyNormalizedContent(source: string | Element, opts?: SetDataOptions): Promise<void> {
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) {
      return Promise.reject(new Error('NgwEditorContent: editor not ready'))
    }
    try {
      // Element 는 setHTML 이 자식을 소비하므로 clone 을 넘겨 아래 fallback 에서 원본을 재사용한다.
      handle.setHTML(typeof source === 'string' ? source : (source.cloneNode(true) as Element))
    } catch (err) {
      const nineMailId = this.adapter.lifecycle.getNineMailId() ?? 'unknown'
      const inner = typeof source === 'string' ? source : source.innerHTML
      const fallbackWrapped =
        `<div class="nine_body" id="nine_body_${nineMailId}" contenteditable="true">` +
        `${inner}</div>`
      try {
        handle.setHTML(fallbackWrapped)
        if (isDev()) {
          console.warn(
            '[NgwEditorContent.setData] legacy draft schema fallback applied — pre-region HTML wrapped in nbody',
            { originalError: err instanceof Error ? err.message : String(err) },
          )
        }
      } catch (fallbackErr) {
        // 두 번째도 실패 — 외부에 reject 전파. 에디터는 마지막 정상 상태 유지 (사용자 draft 손실 차단).
        return Promise.reject(fallbackErr)
      }
    }

    if (opts?.resetUndoHistory === true) {
      // NgwEditor 0.2.10 은 직접 history reset 표면을 노출하지 않는다. setHTML 호출 자체가
      // EditorState 를 새로 만들어 PM history plugin 의 done/undone 스택을 비우므로 추가
      // 작업이 불필요 — pr-n0-inventory §6.1 의 setHTML semantics 확인 결과.
    }
    // opts.restoreFold 는 PR-N4 (mail-regions extension) 가 추가된 후 region 명령으로 처리.
    // 본 PR 단계는 plain 본문 설정만 지원하므로 silent ignore.

    // notifySetDataEnd 핸들러 발화는 PR-N6 의 event wiring 단계에서 추가.
    return Promise.resolve()
  }

  getData(asHTML = true): string {
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) {
      return ''
    }
    if (asHTML) {
      // includeStyles: true — 라운드트립 멱등성 확보 (pr-n0-inventory §6.1 권고).
      return handle.getHTML({ includeStyles: true })
    }
    return handle.getText()
  }

  /**
   * 전송/저장 경로용 본문 직렬화. PR-N9a-2.
   *
   * 1. `handle.getHTML({ includeStyles: true })` — PM doc 동기 직렬화
   * 2. DOMParser 로 임시 DOM 생성 (PM state 무영향)
   * 3. 임시 DOM 의 `<img>` 순회 — `adapter.image.resolveOriginalSrc(dataUrl)` 로
   *    `data:image/...` → 원본 `cid:`/`blob:` 역치환
   * 4. `adapter.getHandler()?.onPreProcess?` 발화 (TinyMCE 시절과 동일 시그니처) —
   *    핸들러가 `evt.node.querySelectorAll('img')` 로 cid 치환 및 inlineAttachmentMap
   *    재구축. handler 미등록 시 역치환만 수행한 결과 반환.
   * 5. mutate 된 `doc.body.innerHTML` 반환.
   *
   * 멱등성: PM state 는 그대로이고 다음 호출 시 같은 getHTML 결과 + 같은 urlMap 으로
   * 같은 역치환이 일어난다. handler 도 매 호출 시 inlineAttachmentMap 을 재구축.
   */
  getDataPreprocessed(): string {
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) {
      return ''
    }
    const html = handle.getHTML({ includeStyles: true })

    const doc = new DOMParser().parseFromString(html, 'text/html')
    const tempNode = doc.body

    // includeStyles 의 <style> 는 DOMParser 가 <head> 로 올려 doc.body 직렬화 시 누락된다.
    // variant(클래스 한정) 규칙만 .ngw-content 스코프 제거해 회수한 뒤, 아래에서 본문에 재주입한다.
    // (본 호출로 doc 내 모든 <style> 는 제거됨.)
    const variantStyleCss = extractVariantStyleCssFromDoc(doc)

    // editor-image-tools (design §8.2, R3-C1) — 전송 픽셀화 적용. 반드시 아래 resolveOriginalSrc
    // 역치환 **앞**에서 수행한다(역치환이 src 를 blobUrl 로 바꾸면 캐시 키(dataUrl) 미스).
    // prepareForSend 가 캐시를 채웠을 때(= 송신 컨텍스트)만 적용 + data-nine-* strip.
    if (this.adapter.image.hasPixelizations()) {
      for (const img of Array.from(tempNode.querySelectorAll('img'))) {
        const src = img.getAttribute('src')
        if (src != null && src.length > 0) {
          const deg = Number.parseInt(img.getAttribute('data-nine-rotate') ?? '0', 10) || 0
          const flipH = img.getAttribute('data-nine-flip-h') === 'true'
          const flipV = img.getAttribute('data-nine-flip-v') === 'true'
          const hit = this.adapter.image.consumePixelized(src, deg, flipH, flipV)
          if (hit != null) {
            // 픽셀화된(이미 회전된 차원의) 비트맵으로 교체. width/height 는 편집 시점 rotate 에서
            // 이미 교환되어 있으므로 송신 시 추가 swap 불요(이중 swap 방지).
            img.setAttribute('src', hit.dataUrl)
          }
        }
        // 송신 직전 회전/내부 메타 속성 제거(TinyMCE _preprocessNode 동일 범위, SE1).
        for (const attr of NINE_IMAGE_META_ATTRS) img.removeAttribute(attr)
      }
    }

    for (const img of Array.from(tempNode.querySelectorAll('img'))) {
      const src = img.getAttribute('src')
      if (src == null || src.length === 0) continue
      const orig = this.adapter.image.resolveOriginalSrc(src)
      if (orig != null) img.setAttribute('src', orig)
    }

    // availability atom 노드의 빈 wrapper 를 self-contained 마크업으로 교체.
    // NgwEditor 의 toDOM 은 attrs 만 직렬화하므로 받는 측 메일 클라이언트가 시간표를 렌더링할
    // 수 없다. TinyMCE 가 본문에 직접 insert 하던 풍부 마크업과 동일한 결과를 송신 직전에 생성.
    //
    // PR-N9a-2 §15-B 재설계 (2026-05-19): 시간표 위·아래 빈 줄은 `addSendAvailability` 명령이
    // atom 노드 양옆에 empty paragraph 를 PM doc 차원에 추가하는 방식으로 처리. 그 paragraph 는
    // getHTML 결과의 `<p><br></p>` 로 자연 직렬화되므로 본 함수에서 wrap 추가가 중복된다. 따라서
    // `wrapWithSpace: false` (기본) 호출 — 본 컨테이너 마크업만 self-contained 로 교체.
    for (const div of Array.from(tempNode.querySelectorAll('div.nine-availability'))) {
      const expanded = buildAvailabilityHtml({
        id: div.getAttribute('data-id') ?? '',
        dateTimeTableJson: div.getAttribute('data-time-tables') ?? '',
        title: div.getAttribute('data-title') ?? '',
        tableDesc: div.getAttribute('data-table-desc') ?? '',
        appDesc: div.getAttribute('data-app-desc') ?? '',
        appLink: div.getAttribute('data-app-link') ?? '',
      })
      if (expanded != null) div.outerHTML = expanded
    }

    const handler = this.adapter.getHandler()
    if (handler?.onPreProcess != null) {
      handler.onPreProcess({ format: 'raw', get: true, node: tempNode })
    }

    // 본문 <style> 처리 (OMD-39 2단계): `extractVariantStyleCssFromDoc` 로 회수한 variant CSS 중
    // **class/id 한정 규칙은 요소 inline 으로 전환**하고(Naver 등 <style> strip 클라이언트에서 variant
    // 테두리/배경 생존), **inline 불가/부적합(가상요소·@media·bare element reset)만 residual <style>** 로
    // 본문 컨테이너 첫 자식에 재주입한다. 그 residual <style> 은 `wrapEmailBodyHTML`(1단계)이 <head> 로
    // 이동시킨다 → text/plain(convert)·서버 preview 오염 차단 + 지원 클라이언트에서 가상요소/@media 적용.
    // bare element(p/blockquote) 를 inline 하지 않는 이유: paragraph 노드 parseDOM 이 margin/padding 을
    // 포착해 재편집 시 attr 로 박히는 taint 회피(base-reset 은 residual <style> 로 유지).
    if (variantStyleCss.length > 0) {
      // 주입/스코프 컨테이너: nine_body / .ngw-content 래퍼가 있으면 그 안에, 없으면 body(tempNode).
      // (firstElementChild 로 넣으면 첫 자식이 <p> 인 flat 본문에서 <style> 가 <p> 내부로 들어가므로 금지.)
      const styleContentRoot =
        tempNode.querySelector('div.nine_body, div[id^="nine_body_"]') ??
        tempNode.querySelector('div.ngw-content') ??
        tempNode
      const residualCss = inlineVariantCssIntoRoot(variantStyleCss, styleContentRoot)
      if (residualCss.trim().length > 0) {
        const injectedStyleEl = doc.createElement('style')
        injectedStyleEl.textContent = residualCss
        styleContentRoot.insertBefore(injectedStyleEl, styleContentRoot.firstChild)
      }
    }

    // docs/editor/mail-body-structure.md §5.1 — 4-region 본문은 spec wrapper 로 감싸 송신.
    // nineMailId 부재 또는 region 부재 시 wrapEmailBodyHTML 가 입력을 그대로 반환 (SignatureEdit 등).
    // sample/test1.html 의 외곽 형태 (`<!DOCTYPE html><html><head><body nine-mail-id style dir>`)
    // 로 감싸 받는 메일 클라이언트가 표준 HTML document 로 렌더링하도록 한다. body style 은
    // setDefaultStyle 로 적용된 fontFamily/fontSize/fontColor + line-height: 1.3 자동 합성.
    const nineMailId = this.adapter.lifecycle.getNineMailId() ?? ''
    const bodyStyle = this.buildBodyStyle()
    return wrapEmailBodyHTML(nineMailId, tempNode.innerHTML, {
      document: { bodyStyle, dir: 'auto' },
    })
  }

  /**
   * `<body style="...">` 의 inline style 합성.
   * setDefaultStyle 로 적용된 fontFamily / fontSize / fontColor 를 기본으로 하고, line-height 는
   * 편집 화면(라이브러리 addStyles)과 동일한 값을 쓰도록 defaults.lineHeight 를 따른다 — 단일
   * source of truth 는 NGW_DEFAULT_LINE_HEIGHT (defaults 미설정 edge 에서만 fallback).
   */
  private buildBodyStyle(): string {
    const defaults = this.adapter.lifecycle.getDefaults()
    const parts: string[] = []
    if (defaults?.fontFamily) parts.push(`font-family: ${defaults.fontFamily}`)
    if (defaults?.fontSize) parts.push(`font-size: ${defaults.fontSize}`)
    if (defaults?.fontColor) parts.push(`color: ${defaults.fontColor}`)
    parts.push(`line-height: ${defaults?.lineHeight ?? NGW_DEFAULT_LINE_HEIGHT}`)
    return parts.join('; ')
  }

  insertContent(text: string): void {
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) {
      return
    }
    // HTML 태그가 포함되었으면 insertHTML, 그 외는 insertText.
    if (/<[a-z][\s\S]*>/i.test(text)) {
      handle.insertHTML(sanitizeImgDimensions(text))
    } else {
      handle.insertText(text)
    }
  }

  modifyData(text: string): void {
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) {
      return
    }
    // TinyMCEContent.modifyData 와 동등 패턴: getHTML 으로 현재 본문 가져와 합성 후 setHTML.
    // 단, 본 PR 단계는 어떤 부분을 수정할지 descriptor 가 없어 단순히 마지막에 append.
    // 실제 호출부 (EmailForm/SignatureEdit) 가 NgwEditor 로 전환되는 PR-N7 이전에 patch 시그니처
    // 의 표면 사용 패턴을 재확인할 예정 (descriptor 기반 modify 가 필요해지면 IEditorContent
    // 시그니처 자체를 보강).
    const current = handle.getHTML({ includeStyles: true })
    handle.setHTML(`${current}${sanitizeImgDimensions(text)}`)
  }

  getBody(): HTMLElement {
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) {
      throw new Error('NgwEditorContent.getBody: editor not ready')
    }
    const dom = handle.getEditorDOM()
    if (dom == null) {
      throw new Error('NgwEditorContent.getBody: editor DOM unavailable')
    }
    return dom
  }

  async setContentURL(url: string, opts?: SetDataOptions): Promise<void> {
    let html: string
    try {
      const response = await fetch(url, { credentials: 'omit' })
      if (!response.ok) {
        console.warn(`NgwEditorContent.setContentURL: HTTP ${response.status} for url ${url}`)
        return
      }
      html = await response.text()
    } catch (err) {
      console.warn('NgwEditorContent.setContentURL: fetch failed', err)
      return
    }
    await this.setData(html, opts)
  }

  setDefaultStyle(style: EditorDefaultStyle): void {
    // EditorDefaultStyle (camelCase) → NgwEditor 의 EditorDefaults 매핑.
    const nextDefaults: EditorDefaults = {
      fontFamily: style.fontFamily,
      fontSize: style.fontSize,
      fontColor: style.color,
      lineHeight: NGW_DEFAULT_LINE_HEIGHT,
    }
    // D-N2 결정: 본문 보존 + key 재마운트. undo 손실 의도된 동작. 직전 본문 캡처/복원은
    // requestRemount → handleEditorReady 가 일괄 처리하므로 여기서는 defaults 만 전달한다.
    this.adapter.lifecycle.requestRemount(nextDefaults)
  }

  isEmpty(): boolean {
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) {
      return true
    }
    return handle.getText().trim().length === 0
  }

  isContentModified(_selector?: string): boolean {
    // 본 PR 단계는 어댑터 내부 dirty 플래그만. selector 분기는 PR-N4 의 region 도입 시점에
    // mail.region.<name>.isChanged() 형태로 표면 분리.
    return this.adapter.isDirty()
  }
}
